import React, { useState, useEffect } from "react";
import LayoutContent from "../../LayoutContent";
import Header from "../../Header/Header";
import Breadcrumb from "../../Header/Breadcrumb";
import HeaderButton from "../../Header/HeaderButton";
import IconDebug from "../../../Icons/TopCommandPanel/Debug";
import ToolsNavSubMenu from "../ToolsNavSubMenu";
import TabPanel from "../../PlateTypeEditor/TabPanel";
import {
  ModuleState,
  ETestingTaskState,
  ETestingState,
  ESuiteMode,
} from "../../../Model/ModuleState";
import { Module } from "../../../Model/Module";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import {
  getSuiteMode,
  getModuleStateById,
  getModuleState,
} from "../../../redux/reducers/ModuleStateReducer";
import { getModules } from "../../../redux/reducers/ModuleReducer";
import { getModuleCapability } from "../../../redux/reducers/ModuleCapabilitiesReducer";
import { EModuleType } from "../../../Model/EModuleType";
import {
  ECarrierAutoCalibrationType,
  EBarcodeScannerType,
} from "../../../Model/PsrCapabilites";
import LiveImage from "../../Imager/LiveImage";
import SwitchButton from "../../Buttons/SwitchButton";
import PsrCameraApi from "../../../api/PsrCameraApi";
import ImagerUtil from "../../../Utility/ImagerUtil";
import LiveStreamHeaderButton from "../../Common/LiveStreamHeaderButton";
import TestingImagerCommandPanel from "./Imager/TestingImagerCommandPanel";
import TestingPsrCommandPanel from "./Psr/TestingPsrCommandPanel";
import TestingMovementContent from "./Movement/TestingMovementContent";
import PsrCameraLiveImage from "../../Settings/Control/PsrCameraLiveImage";
import { NoImage } from "../../../Icons/LiveImage";

type TestingPageType = "IMAGER TESTING" | "PSR TESTING" | "MOVEMENT TESTING";

const testingCatLocalStorageKey = "TESTING_CATEGORY_LAST_SELECTED";

const testingPageTabs: TestingPageType[] = [
  "IMAGER TESTING",
  "PSR TESTING",
  "MOVEMENT TESTING",
];

function IsTestingActive(state?: ETestingState) {
  return (
    state === ETestingState.Started ||
    state === ETestingState.Starting ||
    state === ETestingState.Stopping
  );
}

function getTestingCategory(
  moduleStateById: { [index: string]: ModuleState },
  modules: Module[]
): ETestingTaskState | null {
  const suiteTesting = moduleStateById["Suite"].suite?.testing;
  if (suiteTesting != null) {
    if (IsTestingActive(suiteTesting.plateBump?.state)) {
      return ETestingTaskState.PlateBump;
    } else if (IsTestingActive(suiteTesting.imaging?.state)) {
      return ETestingTaskState.Imaging;
    } else if (IsTestingActive(suiteTesting.movement?.state)) {
      return ETestingTaskState.Movement;
    } else if (IsTestingActive(suiteTesting.barcodeScan?.state)) {
      return ETestingTaskState.Barcode;
    } else if (IsTestingActive(suiteTesting.reliability?.state)) {
      return ETestingTaskState.Reliability;
    }
  }

  for (let i = 0; i < modules.length; i++) {
    const testing = moduleStateById[modules[i].identifier].imager?.testing;
    if (testing == null) {
      continue;
    }
    if (IsTestingActive(testing.autoExposure?.state)) {
      return ETestingTaskState.AutoExposure;
    } else if (IsTestingActive(testing.condenser?.state)) {
      return ETestingTaskState.Condenser;
    } else if (IsTestingActive(testing.lightPathSwitching?.state)) {
      return ETestingTaskState.LightpathSwitching;
    } else if (IsTestingActive(testing.objectiveSwitching?.state)) {
      return ETestingTaskState.ObjectiveSwitching;
    }
  }

  const localStorageStateString = localStorage.getItem(
    testingCatLocalStorageKey
  );
  if (localStorageStateString !== null) {
    const localStorageState = parseInt(
      localStorageStateString
    ) as ETestingTaskState;
    return localStorageState;
  }

  return null;
}

function getActiveTab(activeCategory: ETestingTaskState) {
  switch (activeCategory) {
    case ETestingTaskState.AutoExposure:
    case ETestingTaskState.Condenser:
    case ETestingTaskState.Imaging:
    case ETestingTaskState.LightpathSwitching:
    case ETestingTaskState.PlateBump:
    case ETestingTaskState.ObjectiveSwitching:
      return "IMAGER TESTING" as TestingPageType;
    case ETestingTaskState.Barcode:
    case ETestingTaskState.Reliability:
      return "PSR TESTING" as TestingPageType;
    case ETestingTaskState.Movement:
      return "MOVEMENT TESTING" as TestingPageType;
    default:
      return "IMAGER TESTING" as TestingPageType;
  }
}

export default function Testing() {
  const suiteMode = useTypedSelector((state) => getSuiteMode(state));
  const [activeTab, setActiveTab] = useState(
    "IMAGER TESTING" as TestingPageType
  );
  const modules = useTypedSelector((state) => getModules(state));
  const moduleStateById = useTypedSelector((state) =>
    getModuleStateById(state)
  );
  const [activeCategory, setActiveCategory] = useState(
    getTestingCategory(moduleStateById, modules) ?? ETestingTaskState.Imaging
  );
  const isDisabled = suiteMode !== ESuiteMode.Normal;

  const selectedTabIndex = testingPageTabs.findIndex((h) => h === activeTab);

  const onClickTab = (header: string) => {
    const testingPageType = header as TestingPageType;
    if (!isDisabled) {
      setActiveTab(testingPageType);
      switch (testingPageType) {
        case "IMAGER TESTING":
          updateActiveCategory(ETestingTaskState.Imaging);
          break;
        case "PSR TESTING":
          updateActiveCategory(ETestingTaskState.Reliability);
          break;
        case "MOVEMENT TESTING":
          updateActiveCategory(ETestingTaskState.Movement);
        default:
          break;
      }
    }
  };

  const updateActiveCategory = (category: ETestingTaskState) => {
    setActiveCategory(category);
    localStorage.setItem(testingCatLocalStorageKey, category.toString());
  };

  useEffect(() => {
    if (suiteMode === ESuiteMode.Testing) {
      let suiteTestingState = getTestingCategory(moduleStateById, modules);
      if (suiteTestingState != null) {
        updateActiveCategory(suiteTestingState);
      }
    }
  }, [suiteMode, moduleStateById, modules]);

  useEffect(() => {
    setActiveTab(getActiveTab(activeCategory));
  }, [activeCategory]);

  const psrModules = useTypedSelector((state) =>
    getModules(state, EModuleType.PSR)
  );
  const psrModule = psrModules[0];

  if (psrModule === undefined) {
    throw new Error("Psr Module is undefined");
  }

  const psrIdentifier = psrModule.identifier;
  const psrCapability = useTypedSelector((state) =>
    getModuleCapability(state, psrIdentifier)
  ).psr;

  if (psrCapability === undefined) {
    throw new Error("Psr Capability is undefined");
  }

  // need change this logic when multi Psr Camera implemented
  const carrierAutoCalibrationCapability = psrCapability.carrierAutoCalibration;
  const carrierBarcodeScannerCapability = psrCapability.barcodeScanner;
  let psrCameraId = -1; // psr camera is not used in barcodeScanner / AutoCalibration
  if (
    carrierAutoCalibrationCapability?.carrierAutoCalibrationType ===
    ECarrierAutoCalibrationType.PsrCamera
  ) {
    psrCameraId = carrierAutoCalibrationCapability.psrCameraId;
  }
  if (
    carrierBarcodeScannerCapability.barcodeScannerType ===
    EBarcodeScannerType.PsrCamera
  ) {
    psrCameraId = carrierBarcodeScannerCapability.psrCameraId;
  }

  const moduleState = useTypedSelector((state) =>
    getModuleState(state, psrIdentifier)
  );

  const psrState = moduleState.psr;

  if (psrState === undefined) {
    throw new Error("Psr state is null");
  }

  const psrCameraState = psrState.psrCameras?.find(
    (x) => x.psrCameraId === psrCameraId
  );

  const imagerModules = useTypedSelector((state) =>
    getModules(state, EModuleType.IMAGER)
  );

  const imagerIdentifier = imagerModules[0].identifier;

  const freerunCapture = useTypedSelector(
    (state) =>
      getModuleState(state, imagerIdentifier)?.imager?.freerunCapture ?? false
  );

  const suiteState = useTypedSelector(
    (state) => getModuleState(state, "Suite").suite
  );

  const imagerState = useTypedSelector(
    (state) => getModuleState(state, imagerIdentifier).imager
  );

  if (suiteState === undefined) {
    throw new Error("Suite state is null");
  }

  if (imagerState === undefined) {
    throw new Error("Imager state is null");
  }

  const imagerManualControlEnabled = !ImagerUtil.getIsOpticControlDisabled(
    imagerState,
    suiteState
  );

  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[
              {
                title: "Testing",
                to: "/tools/testing",
                disabled: false,
              },
            ]}
          />
          <Header.RightSide>
            {activeTab === "IMAGER TESTING" && (
              <LiveStreamHeaderButton
                freerunCapture={freerunCapture}
                imagerIdentifier={imagerIdentifier}
                disabled={!imagerManualControlEnabled}
              />
            )}
            {activeTab === "PSR TESTING" && psrCameraId > -1 && (
              <HeaderButton
                id={"PSR-CAM-LIVE"}
                label={"Live Stream"}
                content={
                  <SwitchButton
                    isOn={psrCameraState?.isStreaming ?? false}
                    withLetter={true}
                    onToggle={() => {
                      PsrCameraApi.SetStreaming(
                        psrIdentifier,
                        psrCameraId,
                        !(psrCameraState?.isStreaming ?? false)
                      );
                    }}
                  />
                }
                onClick={() => {
                  PsrCameraApi.SetStreaming(
                    psrIdentifier,
                    psrCameraId,
                    !(psrCameraState?.isStreaming ?? false)
                  );
                }}
                disabled={false}
              />
            )}
            <HeaderButton
              id={"DEBUG_PAGE"}
              label="Debug"
              icon={<IconDebug />}
              onClick={(id) => {
                window.open("/debug", "debugpage");
              }}
            />
          </Header.RightSide>
        </Header>
      </LayoutContent.Header>
      <LayoutContent.SubMenu>
        <ToolsNavSubMenu />
      </LayoutContent.SubMenu>
      <LayoutContent.Content
        style={{
          paddingTop: 0,
          paddingRight: activeTab === "MOVEMENT TESTING" ? 16 : 0,
        }}
      >
        <TabPanel
          id="testing_tab"
          collapsible={false}
          variant="alternate"
          headerHeight={48}
          style={{ height: "100%", marginLeft: 12 }}
          onClickTab={onClickTab}
          allowChange={false}
          defaultSelectedIndex={selectedTabIndex}
          headerItemWidth="auto"
          activeTabStyle={{
            padding: "0px",
          }}
          headerStyle={{
            margin: "0px 0px",
            paddingLeft: 16,
            paddingRight: 16,
            letterSpacing: 1.25,
            whiteSpace: "nowrap",
          }}
        >
          {{
            "IMAGER TESTING": (
              <LiveImage
                imagerIdentifier={imagerIdentifier}
                style={{
                  borderTop: "1px solid #B4B4B4",
                  borderLeft: "1px solid #B4B4B4",
                }}
              />
            ),
            "PSR TESTING":
              psrCameraId > -1 ? (
                <PsrCameraLiveImage
                  psrCameraId={psrCameraId}
                  psrIdentifier={psrIdentifier}
                  style={{
                    borderTop: "1px solid #B4B4B4",
                    borderLeft: "1px solid #B4B4B4",
                  }}
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    height: "100%",
                    borderTop: "solid 1px #B4B4B4",
                    borderLeft: "solid 1px #B4B4B4",
                  }}
                >
                  <NoImage width="251px" height="336px" />
                  <div
                    style={{
                      fontSize: 32,
                      fontFamily: "Roboto",
                      color: "#CACACA",
                    }}
                  >
                    Live Stream unavailable in this feature
                  </div>
                </div>
              ),
            "MOVEMENT TESTING": <TestingMovementContent />,
          }}
        </TabPanel>
      </LayoutContent.Content>
      {activeTab !== "MOVEMENT TESTING" && (
        <LayoutContent.CommandPanel
          style={{
            paddingTop: 49,
            borderTop: "6px solid rgb(130, 130, 130)",
            WebkitBoxShadow: "none",
            MozBoxShadow: "none",
            boxShadow: "none",
            paddingRight: 16,
          }}
        >
          {activeTab === "IMAGER TESTING" && (
            <TestingImagerCommandPanel
              activeCategory={activeCategory}
              imagerIdentifier={imagerIdentifier}
              setActiveCategory={updateActiveCategory}
            />
          )}
          {activeTab === "PSR TESTING" && (
            <TestingPsrCommandPanel
              activeCategory={activeCategory}
              setActiveCategory={updateActiveCategory}
              psrIdentifier={psrIdentifier}
              psrCameraId={psrCameraId}
            />
          )}
        </LayoutContent.CommandPanel>
      )}
    </LayoutContent>
  );
}
